import type { AnyFieldApi } from '@tanstack/react-form';
import type { ComponentType, HTMLAttributes, ReactNode } from 'react';

import { t } from '@lingui/core/macro';
import { useField } from '@tanstack/react-form';
import { Eye } from '@untitledui/icons/Eye';
import { EyeOff } from '@untitledui/icons/EyeOff';
import { useId, useState } from 'react';
import { Label } from 'react-aria-components';

import { Checkbox } from '@/components/base/checkbox/checkbox';
import { PhoneNumberInput } from '@/components/base/form/phone-number-input';
import { HintText } from '@/components/base/input/hint-text';
import { Input } from '@/components/base/input/input';
import {
  RadioButton,
  RadioGroup,
} from '@/components/base/radio-buttons/radio-buttons';
import {
  MultiSelectDropdown,
  type MultiSelectDropdownItem,
} from '@/components/base/select/multi-select-dropdown';
import { Select, type SelectItemType } from '@/components/base/select/select';
import { SelectItem } from '@/components/base/select/select-item';
import { TextArea } from '@/components/base/textarea/textarea';
import { Toggle } from '@/components/base/toggle/toggle';
import { getErrorMessage } from '@/utils/error';

import type { BaseProps } from './base-props';

import { SliderFormInput, type SliderInputProps } from './slider-input';
import { FormAutoResizeTextarea } from './textarea-input';

interface TextInputProps<TFormData> extends BaseProps<TFormData> {
  type: 'text' | 'email' | 'password' | 'phoneNumber';
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  size?: 'xs' | 'sm' | 'md';
  tooltip?: string;
  icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
  trailingIcon?: ReactNode;
  /** Called with the field's value after it loses focus. */
  onBlur?: (value: string) => void;
  /**
   * When `type` is `password`, show a trailing control to toggle visibility.
   * @default true for password fields
   */
  showPasswordToggle?: boolean;
  /** `phoneNumber` only: ISO country code preselected for an empty value. */
  defaultCountry?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  wrapperClassName?: string;
}

interface TextareaInputProps<TFormData> extends BaseProps<TFormData> {
  type: 'textarea';
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
  labelClassName?: string;
  textAreaClassName?: string;
  /** Auto-grow height to fit content (inline modal-style fields). */
  autoResize?: boolean;
  /** Show left border when empty (outline preview style). */
  showEmptyHighlight?: boolean;
}

interface ToggleInputProps<TFormData> extends BaseProps<TFormData> {
  type: 'toggle';
  size?: 'xs' | 'sm' | 'md';
  slim?: boolean;
  isDisabled?: boolean;
  className?: string;
}

interface SelectInputProps<
  TFormData,
  T extends string = string,
> extends BaseProps<TFormData> {
  type: 'select';
  items: SelectItemType<T>[];
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md';
  tooltip?: string;
  isDisabled?: boolean;
  className?: string;
  trailingIcon?: ReactNode;
  children?: (item: SelectItemType<T>) => ReactNode;
}

interface MultiSelectInputProps<
  TFormData,
  T extends string = string,
> extends BaseProps<TFormData> {
  type: 'multiselect';
  items: MultiSelectDropdownItem<T>[];
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md';
  tooltip?: string;
  isDisabled?: boolean;
  maxDisplay?: number;
  className?: string;
  trailingIcon?: ReactNode;
}

interface RadioInputProps<TFormData> extends BaseProps<TFormData> {
  type: 'radio';
  trueLabel: string;
  falseLabel: string;
  isDisabled?: boolean;
  className?: string;
  trailingIcon?: ReactNode;
  size?: 'xs' | 'sm' | 'md';
}

interface CheckboxInputProps<TFormData> extends Omit<
  BaseProps<TFormData>,
  'label'
> {
  type: 'checkbox';
  label?: ReactNode;
  size?: 'xs' | 'sm' | 'md';
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
}

export type FormInputProps<TFormData, T extends string = string> =
  | TextInputProps<TFormData>
  | TextareaInputProps<TFormData>
  | ToggleInputProps<TFormData>
  | SelectInputProps<TFormData, T>
  | MultiSelectInputProps<TFormData, T>
  | RadioInputProps<TFormData>
  | CheckboxInputProps<TFormData>
  | SliderInputProps<TFormData>;

/**
 * Returns a field's first error once the user has interacted with it
 * (blurred) or attempted to submit. Pass `submissionAttempts` explicitly
 * when calling from a `form.Field` render prop — those don't re-subscribe
 * to form-level state, so the field's own `field.form.state` won't update.
 *
 * Falls back to form-level errors (TanStack Form keeps Standard Schema
 * issues in `form.state.errorMap` keyed by field name) so untouched fields
 * still surface errors after a submit attempt.
 */
export function getFieldError(
  field: AnyFieldApi,
  submissionAttempts?: number,
): string | undefined {
  const isBlurred = field.state.meta.isBlurred;
  const hasSubmitted =
    (submissionAttempts ?? field.form.state.submissionAttempts) > 0;
  if (!isBlurred && !hasSubmitted) return undefined;

  const fieldError = field.state.meta.errors[0];
  if (fieldError) return getErrorMessage(fieldError);

  const formError = findFormIssue(
    field.form.state.errorMap,
    field.name,
    field.state.meta.isDirty,
  );
  return formError ? getErrorMessage(formError) : undefined;
}

function findFormIssue(
  errorMap: unknown,
  fieldName: string,
  isDirty: boolean,
): unknown {
  if (!isRecord(errorMap)) return undefined;
  const triggers = isDirty
    ? ['onChange', 'onBlur']
    : ['onSubmit', 'onChange', 'onBlur', 'onMount'];
  for (const trigger of triggers) {
    const issues = isRecord(errorMap[trigger])
      ? errorMap[trigger][fieldName]
      : undefined;
    if (Array.isArray(issues) && issues.length > 0) return issues[0];
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function FormInput<TFormData, T extends string = string>(
  props: FormInputProps<TFormData, T>,
) {
  const { form, field: name, hint } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fieldApi = useField({ form, name } as any);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const checkboxHintDescriptionId = useId();

  const errorMessage = getFieldError(fieldApi);
  const showError = !!errorMessage;
  const displayHint = showError ? errorMessage : hint;

  switch (props.type) {
    case 'phoneNumber':
      return (
        <PhoneNumberInput
          label={props.label}
          hint={displayHint}
          placeholder={props.placeholder}
          value={String(fieldApi.state.value ?? '')}
          onChange={(v) => fieldApi.handleChange(v)}
          onBlur={fieldApi.handleBlur}
          isInvalid={showError}
          isDisabled={props.isDisabled}
          isRequired={props.isRequired}
          size={props.size}
          tooltip={props.tooltip}
          className={props.className}
          defaultCountry={props.defaultCountry}
        />
      );
    case 'text':
    case 'email':
      return (
        <Input
          type={props.type === 'email' ? 'email' : undefined}
          label={props.label}
          placeholder={props.placeholder}
          // Coerce nullish field state to '' so the input is controlled from the
          // first render. A raw undefined value renders an uncontrolled input
          // that flips to controlled on first keystroke (React warns).
          value={String(fieldApi.state.value ?? '')}
          onChange={(v) => fieldApi.handleChange(v)}
          onBlur={() => {
            fieldApi.handleBlur();
            props.onBlur?.(String(fieldApi.state.value ?? ''));
          }}
          isInvalid={showError}
          isDisabled={props.isDisabled}
          isRequired={props.isRequired}
          hint={displayHint}
          size={props.size}
          tooltip={props.tooltip}
          icon={props.icon}
          trailingIcon={props.trailingIcon}
          className={props.className}
          labelClassName={props.labelClassName}
          inputClassName={props.inputClassName}
          wrapperClassName={props.wrapperClassName}
        />
      );

    case 'password': {
      const showToggle = props.showPasswordToggle !== false;
      const trailingIcon = showToggle ? (
        <>
          {props.trailingIcon}
          <button
            type="button"
            className="cursor-pointer rounded text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={passwordVisible ? t`Hide password` : t`Show password`}
            aria-pressed={passwordVisible}
            disabled={props.isDisabled}
            onClick={() => setPasswordVisible((v) => !v)}
          >
            {passwordVisible ? (
              <EyeOff className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </button>
        </>
      ) : (
        props.trailingIcon
      );

      return (
        <Input
          type={showToggle && passwordVisible ? 'text' : 'password'}
          label={props.label}
          placeholder={props.placeholder}
          value={String(fieldApi.state.value ?? '')}
          onChange={(v) => fieldApi.handleChange(v)}
          onBlur={fieldApi.handleBlur}
          isInvalid={showError}
          isDisabled={props.isDisabled}
          isRequired={props.isRequired}
          hint={displayHint}
          size={props.size}
          tooltip={props.tooltip}
          icon={props.icon}
          trailingIcon={trailingIcon}
          className={props.className}
          labelClassName={props.labelClassName}
          inputClassName={props.inputClassName}
          wrapperClassName={props.wrapperClassName}
        />
      );
    }

    case 'textarea': {
      const stringValue = String(fieldApi.state.value ?? '');

      if (props.autoResize) {
        return (
          <FormAutoResizeTextarea
            value={stringValue}
            onChange={(v) => fieldApi.handleChange(v)}
            onBlur={fieldApi.handleBlur}
            label={props.label}
            placeholder={props.placeholder}
            isDisabled={props.isDisabled}
            isInvalid={showError}
            displayHint={displayHint}
            showError={showError}
            textAreaClassName={props.textAreaClassName}
            labelClassName={props.labelClassName}
            showEmptyHighlight={props.showEmptyHighlight}
            className={props.className}
            isRequired={props.isRequired}
          />
        );
      }

      return (
        <TextArea
          label={props.label}
          placeholder={props.placeholder}
          value={stringValue}
          onChange={(v) => fieldApi.handleChange(v)}
          onBlur={fieldApi.handleBlur}
          isInvalid={showError}
          isDisabled={props.isDisabled}
          isRequired={props.isRequired}
          hint={displayHint}
          className={props.className}
          labelClassName={props.labelClassName}
          textAreaClassName={props.textAreaClassName}
        />
      );
    }

    case 'toggle':
      return (
        <Toggle
          label={props.label}
          hint={displayHint}
          isSelected={fieldApi.state.value as boolean}
          onToggle={() =>
            fieldApi.handleChange(!(fieldApi.state.value as boolean))
          }
          size={props.size}
          slim={props.slim}
          isDisabled={props.isDisabled}
          className={props.className}
        />
      );

    case 'select':
      return (
        <Select
          label={props.label}
          value={fieldApi.state.value as T}
          onSelectionChange={(value) => fieldApi.handleChange(value)}
          items={props.items}
          placeholder={props.placeholder}
          isInvalid={showError}
          isDisabled={props.isDisabled}
          hint={displayHint as string | undefined}
          size={props.size}
          tooltip={props.tooltip}
          trailingIcon={props.trailingIcon}
          className={props.className}
        >
          {props.children ??
            ((item: SelectItemType<T>) => (
              <SelectItem key={item.id} {...item} />
            ))}
        </Select>
      );

    case 'multiselect':
      return (
        <MultiSelectDropdown
          label={props.label}
          selectedKeys={(fieldApi.state.value as T[]) ?? []}
          onSelectionChange={(keys) => fieldApi.setValue([...keys])}
          items={props.items}
          placeholder={props.placeholder}
          isInvalid={showError}
          isDisabled={props.isDisabled}
          hint={displayHint as string | undefined}
          size={props.size}
          tooltip={props.tooltip}
          maxDisplay={props.maxDisplay}
          trailingIcon={props.trailingIcon}
          className={props.className}
        />
      );

    case 'radio': {
      const boolValue = fieldApi.state.value as boolean | undefined;
      return (
        <div className="flex flex-col gap-1.5">
          {(props.label || props.trailingIcon) && (
            <div className="flex items-center gap-1">
              {props.label && (
                <Label className="text-sm font-medium text-secondary">
                  {props.label}
                </Label>
              )}
              {props.trailingIcon}
            </div>
          )}
          <RadioGroup
            value={
              boolValue === true
                ? 'true'
                : boolValue === false
                  ? 'false'
                  : undefined
            }
            onChange={(v) => fieldApi.handleChange(v === 'true')}
            orientation="horizontal"
            isDisabled={props.isDisabled}
            className="flex-row gap-4"
            size={props.size}
          >
            <RadioButton value="true" label={props.trueLabel} />
            <RadioButton value="false" label={props.falseLabel} />
          </RadioGroup>
          {displayHint && (
            <HintText isInvalid={showError}>{displayHint}</HintText>
          )}
        </div>
      );
    }

    case 'checkbox':
      return (
        <Checkbox
          size={props.size ?? 'sm'}
          isSelected={fieldApi.state.value as boolean}
          onChange={(checked) => fieldApi.handleChange(checked)}
          onBlur={fieldApi.handleBlur}
          isInvalid={showError}
          aria-describedby={showError ? checkboxHintDescriptionId : undefined}
          hintId={showError ? checkboxHintDescriptionId : undefined}
          label={props.label}
          hint={
            showError ? (
              <span className="text-error-primary">{errorMessage}</span>
            ) : (
              hint
            )
          }
          isDisabled={props.isDisabled}
          isRequired={props.isRequired}
          className={props.className}
        />
      );

    case 'slider':
      return (
        <SliderFormInput
          rawFieldValue={fieldApi.state.value as number}
          onChange={(v) => fieldApi.handleChange(v)}
          onBlur={fieldApi.handleBlur}
          {...props}
        >
          {displayHint && (
            <HintText isInvalid={showError} className="mt-1">
              {displayHint}
            </HintText>
          )}
        </SliderFormInput>
      );
  }
}
